import React, { useState, useEffect, useRef } from "react";
import { GenerationSection } from "./GenerateDraftSidebar";
import "./TreatmentPlanSection.css";

interface TreatmentPlanSectionProps {
  section: GenerationSection;
  data: any;
  editable?: boolean;
  onDataChange?: (newData: any) => void;
}

interface EditModalState {
  isOpen: boolean;
  field: string;
  value: string;
  title: string;
}

const TreatmentPlanSection: React.FC<TreatmentPlanSectionProps> = ({
  section,
  data,
  editable = false,
  onDataChange,
}) => {
  // Local state for editing
  const [localData, setLocalData] = useState<any>(data);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [modalState, setModalState] = useState<EditModalState | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync local data when prop changes, but NOT while editing
  // (to prevent focus loss when parent state updates)
  useEffect(() => {
    if (!editingField && !modalState?.isOpen) {
      setLocalData(data);
    }
  }, [data, editingField, modalState?.isOpen]);

  // Focus input when editing starts
  useEffect(() => {
    if (editingField && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingField]);

  // Update a field in the data
  const updateField = (field: string, value: any) => {
    const newData = { ...localData, [field]: value };
    setLocalData(newData);
    onDataChange?.(newData);
  };

  // Update nested field (e.g., for arrays)
  const updateNestedField = (arrayField: string, index: number, field: string, value: any) => {
    const newArray = [...(localData[arrayField] || [])];
    newArray[index] = { ...newArray[index], [field]: value };
    const newData = { ...localData, [arrayField]: newArray };
    setLocalData(newData);
    onDataChange?.(newData);
  };

  // Delete a row from an array field
  const deleteArrayItem = (arrayField: string, index: number) => {
    const newArray = [...(localData[arrayField] || [])];
    newArray.splice(index, 1);
    const newData = { ...localData, [arrayField]: newArray };
    setLocalData(newData);
    onDataChange?.(newData);
  };

  // Add a new row at a specific index
  const addArrayItemAt = (arrayField: string, index: number, template: any) => {
    const newArray = [...(localData[arrayField] || [])];
    newArray.splice(index, 0, template);
    const newData = { ...localData, [arrayField]: newArray };
    setLocalData(newData);
    onDataChange?.(newData);
  };

  // Domain identifier mapping for goal numbering (X.Y pattern)
  const DOMAIN_IDENTIFIERS: Record<string, number> = {
    [GenerationSection.LANGUAGE_GOALS]: 1,
    [GenerationSection.SOCIAL_AND_PLAY_GOALS]: 2,
    [GenerationSection.ATTENDING_GOALS]: 3,
    [GenerationSection.DAILY_LIVING_GOALS]: 4,
    [GenerationSection.SAFETY_GOALS]: 5,
    [GenerationSection.CAREGIVER_GOALS]: 6,
  };

  // Get domain identifier for current section
  const getDomainId = () => DOMAIN_IDENTIFIERS[section] || 0;

  // Centralized row templates for all editable array fields
  const getRowTemplate = (arrayField: string, index?: number): any => {
    const templates: Record<string, any> = {
      goals: {
        goal_text: "",
        baseline: "",
        status: "",
        dates: ""
      },
      assessments: { assessment_name: "", details: "", date_completed: "" },
      previous_services: { service_type: "", provider: "", hours_per_month: "" },
      behaviors: { name: "", definition: "", current: "", function: "" },
      domains: { domain_name: "", support_level: "", goal_count: "" },
      service_codes: { code: "", description: "", units: "" }
    };
    return templates[arrayField] || {};
  };

  // Simplified row controls - derives template automatically
  const renderRowControls = (arrayField: string, index: number) => {
    if (!editable) return null;
    return (
      <div className="row-controls">
        <button
          className="row-control-btn add-btn add-top"
          onClick={(e) => {
            e.stopPropagation();
            addArrayItemAt(arrayField, index, getRowTemplate(arrayField, index));
          }}
          title="Add row above"
        >+</button>
        <button
          className="row-control-btn delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            deleteArrayItem(arrayField, index);
          }}
          title="Delete row"
        >−</button>
        <button
          className="row-control-btn add-btn add-bottom"
          onClick={(e) => {
            e.stopPropagation();
            addArrayItemAt(arrayField, index + 1, getRowTemplate(arrayField, index + 1));
          }}
          title="Add row below"
        >+</button>
      </div>
    );
  };


  // Handle click on editable field
  const handleFieldClick = (field: string, value: string, title: string) => {
    if (!editable) return;
    
    const strValue = value || "";
    // For long text (>30 chars) or multi-line content, open modal
    if (strValue.length > 30 || strValue.includes("\n")) {
      setModalState({
        isOpen: true,
        field,
        value: strValue,
        title,
      });
    } else {
      // For short text, inline edit
      setEditingField(field);
      setEditValue(strValue);
    }
  };

  // Handle click on nested editable field
  const handleNestedFieldClick = (
    arrayField: string,
    index: number,
    field: string,
    value: string,
    title: string
  ) => {
    if (!editable) return;
    
    const strValue = value || "";
    const fieldPath = `${arrayField}[${index}].${field}`;
    
    if (strValue.length > 30 || strValue.includes("\n")) {
      setModalState({
        isOpen: true,
        field: fieldPath,
        value: strValue,
        title,
      });
    } else {
      setEditingField(fieldPath);
      setEditValue(strValue);
    }
  };

  // Save inline edit
  const saveInlineEdit = () => {
    if (!editingField) return;
    
    // Check if it's a nested field
    const nestedMatch = editingField.match(/^(.+)\[(\d+)\]\.(.+)$/);
    if (nestedMatch) {
      const [, arrayField, indexStr, field] = nestedMatch;
      updateNestedField(arrayField, parseInt(indexStr), field, editValue);
    } else {
      updateField(editingField, editValue);
    }
    
    setEditingField(null);
    setEditValue("");
  };

  // Cancel inline edit
  const cancelInlineEdit = () => {
    setEditingField(null);
    setEditValue("");
  };

  // Handle key press in inline edit
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      saveInlineEdit();
    } else if (e.key === "Escape") {
      cancelInlineEdit();
    }
  };

  // Save modal edit
  const saveModalEdit = () => {
    if (!modalState) return;
    
    const nestedMatch = modalState.field.match(/^(.+)\[(\d+)\]\.(.+)$/);
    if (nestedMatch) {
      const [, arrayField, indexStr, field] = nestedMatch;
      updateNestedField(arrayField, parseInt(indexStr), field, modalState.value);
    } else if (modalState.field === "_narrative") {
      // Special case for narrative sections
      if (typeof localData === "string") {
        setLocalData(modalState.value);
        onDataChange?.(modalState.value);
      } else {
        updateField("text", modalState.value);
      }
    } else {
      updateField(modalState.field, modalState.value);
    }
    
    setModalState(null);
  };

  // Cancel modal edit
  const cancelModalEdit = () => {
    setModalState(null);
  };

  // Handle blur - discard changes unless clicking on save/cancel buttons
  const handleBlur = (e: React.FocusEvent) => {
    // Check if focus moved to a button within the inline-editor
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget?.closest('.inline-editor')) {
      return; // Don't cancel if clicking save/cancel buttons
    }
    cancelInlineEdit();
  };

  // Render helper for editable field - returns JSX directly (not a component)
  // This avoids re-mounting issues that cause focus loss
  const renderEditableField = (
    value: string,
    field: string,
    title: string = "Edit Field",
    className: string = ""
  ) => {
    const isEditing = editingField === field;
    
    if (isEditing) {
      return (
        <span className="inline-editor">
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
          />
          <button className="save-btn" onClick={saveInlineEdit} title="Save (Enter)">✓</button>
          <button className="cancel-btn" onClick={cancelInlineEdit} title="Cancel (Esc)">✕</button>
        </span>
      );
    }
    
    return (
      <span
        className={`editable-field ${editable ? "editable-field--active" : ""} ${className}`}
        onClick={() => handleFieldClick(field, value, title)}
        title={editable ? "Click to edit" : undefined}
      >
        {value || "—"}
      </span>
    );
  };

  // Render helper for nested editable field - returns JSX directly
  const renderEditableNestedField = (
    value: string,
    arrayField: string,
    index: number,
    field: string,
    title: string = "Edit Field",
    className: string = ""
  ) => {
    const fieldPath = `${arrayField}[${index}].${field}`;
    const isEditing = editingField === fieldPath;
    
    if (isEditing) {
      return (
        <span className="inline-editor">
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
          />
          <button className="save-btn" onClick={saveInlineEdit} title="Save (Enter)">✓</button>
          <button className="cancel-btn" onClick={cancelInlineEdit} title="Cancel (Esc)">✕</button>
        </span>
      );
    }
    
    return (
      <span
        className={`editable-field ${editable ? "editable-field--active" : ""} ${className}`}
        onClick={() => handleNestedFieldClick(arrayField, index, field, value, title)}
        title={editable ? "Click to edit" : undefined}
      >
        {value || "—"}
      </span>
    );
  };

  // Helper function to extract text from various formats
  const extractText = (data: any): string => {
    if (typeof data === "string") {
      return data;
    }
    if (data && typeof data === "object" && data.text) {
      return data.text;
    }
    return JSON.stringify(data);
  };

  // Helper function to render editable narrative content
  const renderNarrative = (data: any) => {
    const text = extractText(data);
    
    const handleNarrativeClick = () => {
      if (!editable) return;
      setModalState({
        isOpen: true,
        field: "_narrative",
        value: typeof localData === "string" ? localData : (localData?.text || text),
        title: "Edit Content",
      });
    };

    return (
      <div
        className={`narrative-content ${editable ? "editable-narrative" : ""}`}
        onClick={handleNarrativeClick}
        title={editable ? "Click to edit" : undefined}
      >
        {text.split("\n\n").map((paragraph: string, index: number) => (
          <p key={index}>{paragraph}</p>
        ))}
      </div>
    );
  };

  // Helper function to render goals table
  const renderGoalsTable = (goals: any[]) => {
    const domainId = getDomainId();
    return (
      <div className="table-wrapper">
        <table className="treatment-plan-table goals-table">
          <thead>
            <tr>
              <th>Goal #</th>
              <th>Goal</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {goals.map((goal, index) => (
              <tr key={index} className="editable-row-container">
                <td className="goal-number">
                  {renderRowControls("goals", index)}
                  <span>{domainId}.{index + 1}</span>
                </td>
                <td>
                  <div className="goal-content">
                    <p className="goal-text">
                      {renderEditableNestedField(goal.goal_text, "goals", index, "goal_text", "Goal Text")}
                    </p>
                    <p className="goal-baseline">
                      <strong>Baseline:</strong>{" "}
                      {renderEditableNestedField(goal.baseline, "goals", index, "baseline", "Baseline")}
                    </p>
                  </div>
                </td>
                <td className="goal-status">
                  {editingField === `goals[${index}].status` ? (
                    <span className="inline-editor" onClick={(e) => e.stopPropagation()}>
                      <input
                        ref={inputRef}
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onBlur={handleBlur}
                      />
                      <button className="save-btn" onClick={saveInlineEdit} title="Save (Enter)">✓</button>
                      <button className="cancel-btn" onClick={cancelInlineEdit} title="Cancel (Esc)">✕</button>
                    </span>
                  ) : (
                    <span
                      className={`status-badge ${editable ? "editable-field editable-field--active" : ""} status-${goal.status?.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => handleNestedFieldClick("goals", index, "status", goal.status || "", "Status")}
                      title={editable ? "Click to edit" : undefined}
                    >
                      {goal.status || "—"}
                    </span>
                  )}
                  {(goal.dates || editable) && (
                    <div className="goal-dates">
                      {renderEditableNestedField(goal.dates || "", "goals", index, "dates", "Dates")}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderSection = () => {
    switch (section) {
      case GenerationSection.DOCUMENT_HEADER:
        if (!localData) return null;
        return (
          <div className="section-content document-header-info">
            <h2>Document Header</h2>
            <table className="treatment-plan-table info-table">
              <tbody>
                <tr>
                  <td colSpan={2}><strong>Document Title:</strong></td>
                  <td colSpan={2}>{renderEditableField(localData.document_title, "document_title", "Document Title")}</td>
                </tr>
                <tr>
                  <td><strong>Date of Report:</strong></td>
                  <td>{renderEditableField(localData.date_of_report, "date_of_report", "Date of Report")}</td>
                  <td><strong>Authorization Period:</strong></td>
                  <td>{renderEditableField(localData.authorization_period, "authorization_period", "Authorization Period")}</td>
                </tr>
              </tbody>
            </table>
          </div>
        );

      case GenerationSection.PATIENT_INFORMATION:
        if (!localData) return null;
        return (
          <div className="section-content demographics">
            <h2>Patient Information</h2>
            <table className="treatment-plan-table info-table">
              <tbody>
                <tr>
                  <td><strong>Patient Name:</strong></td>
                  <td>{renderEditableField(localData.patient_name, "patient_name", "Patient Name")}</td>
                  <td><strong>Date of Birth:</strong></td>
                  <td>{renderEditableField(localData.date_of_birth, "date_of_birth", "Date of Birth")}</td>
                </tr>
                <tr>
                  <td><strong>Legal Guardian(s):</strong></td>
                  <td>{renderEditableField(localData.legal_guardians, "legal_guardians", "Legal Guardians")}</td>
                  <td><strong>Address:</strong></td>
                  <td>{renderEditableField(localData.address, "address", "Address")}</td>
                </tr>
                <tr>
                  <td><strong>Member ID:</strong></td>
                  <td>{renderEditableField(localData.member_id, "member_id", "Member ID")}</td>
                  <td><strong>Assigned Supervisor:</strong></td>
                  <td>{renderEditableField(localData.assigned_supervisor, "assigned_supervisor", "Assigned Supervisor")}</td>
                </tr>
              </tbody>
            </table>
          </div>
        );

      case GenerationSection.DIAGNOSTIC_INFORMATION:
        if (!localData) return null;
        return (
          <div className="section-content diagnostic">
            <h2>Diagnostic Information</h2>
            <table className="treatment-plan-table info-table">
              <tbody>
                <tr>
                  <td><strong>Date of Diagnosis:</strong></td>
                  <td>{renderEditableField(localData.date_of_diagnosis, "date_of_diagnosis", "Date of Diagnosis")}</td>
                  <td><strong>Diagnosing Provider:</strong></td>
                  <td>{renderEditableField(localData.diagnosing_provider, "diagnosing_provider", "Diagnosing Provider")}</td>
                </tr>
                <tr>
                  <td><strong>Age at Diagnosis:</strong></td>
                  <td>{renderEditableField(localData.age_at_diagnosis, "age_at_diagnosis", "Age at Diagnosis")}</td>
                  <td><strong>Primary Diagnosis:</strong></td>
                  <td>{renderEditableField(localData.primary_diagnosis, "primary_diagnosis", "Primary Diagnosis")}</td>
                </tr>
                <tr>
                  <td colSpan={2}><strong>Additional Diagnostic Information:</strong></td>
                  <td colSpan={2}>{renderEditableField(localData.additional_diagnostic_info, "additional_diagnostic_info", "Additional Diagnostic Info")}</td>
                </tr>
                <tr>
                  <td colSpan={2}><strong>Any Known Allergies:</strong></td>
                  <td colSpan={2}>{renderEditableField(localData.known_allergies, "known_allergies", "Known Allergies")}</td>
                </tr>
                <tr>
                  <td colSpan={2}><strong>Current Medications:</strong></td>
                  <td colSpan={2}>{renderEditableField(localData.current_medications, "current_medications", "Current Medications")}</td>
                </tr>
              </tbody>
            </table>
          </div>
        );

      case GenerationSection.HISTORY_OF_SERVICES:
        if (!localData) return null;
        return (
          <div className="section-content history">
            <h2>History of Services</h2>
            <table className="treatment-plan-table info-table">
              <tbody>
                <tr>
                  <td><strong>Initial Assessment Date at ANNA:</strong></td>
                  <td>{renderEditableField(localData.initial_assessment_date, "initial_assessment_date", "Initial Assessment Date")}</td>
                  <td><strong>Recommended Service Model:</strong></td>
                  <td>{renderEditableField(localData.recommended_service_model, "recommended_service_model", "Recommended Service Model")}</td>
                </tr>
                <tr>
                  <td><strong>Proposed Number of Hours:</strong></td>
                  <td>{renderEditableField(localData.proposed_hours, "proposed_hours", "Proposed Hours")}</td>
                  <td><strong>Location of Services:</strong></td>
                  <td>{renderEditableField(localData.location_of_services, "location_of_services", "Location of Services")}</td>
                </tr>
                {localData.previous_services?.map((service: any, index: number) => (
                  <tr key={index} className="editable-row-container">
                    <td>
                      {renderRowControls("previous_services", index)}
                      <strong>
                        {renderEditableNestedField(service.service_type, "previous_services", index, "service_type", "Service Type")}:
                      </strong>
                    </td>
                    <td>
                      {renderEditableNestedField(service.provider, "previous_services", index, "provider", "Provider")}
                    </td>
                    <td><strong>Number of Hours per Month:</strong></td>
                    <td>
                      {renderEditableNestedField(service.hours_per_month, "previous_services", index, "hours_per_month", "Hours per Month")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case GenerationSection.RELEVANT_HISTORY:
        if (!localData) return null;
        return (
          <div className="section-content relevant-history">
            <h2>Relevant History</h2>
            {renderNarrative(localData)}
          </div>
        );

      case GenerationSection.DIRECT_AND_INDIRECT_MEASURES:
        if (!localData) return null;
        return (
          <div className="section-content assessment-measures">
            <h2>Direct and Indirect Measures</h2>
            <div className="table-wrapper">
              <table className="treatment-plan-table">
                <thead>
                  <tr>
                    <th>Assessment</th>
                    <th>Details</th>
                    <th>Date Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {localData.assessments?.map((assessment: any, index: number) => (
                    <tr key={index} className="editable-row-container">
                      <td>
                        {renderRowControls("assessments", index)}
                        {renderEditableNestedField(assessment.assessment_name, "assessments", index, "assessment_name", "Assessment Name")}
                      </td>
                      <td>
                        {renderEditableNestedField(assessment.details, "assessments", index, "details", "Details")}
                      </td>
                      <td>
                        {renderEditableNestedField(assessment.date_completed, "assessments", index, "date_completed", "Date Completed")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case GenerationSection.INITIAL_OBSERVATION:
        if (!localData) return null;
        return (
          <div className="section-content initial-observation-section">
            <h2>Initial Observation</h2>
            {renderNarrative(localData)}
          </div>
        );

      case GenerationSection.LANGUAGE_DOMAIN:
        if (!localData) return null;
        return (
          <div className="section-content domain-analysis">
            <h2>Domain: Language</h2>
            {renderNarrative(localData)}
          </div>
        );

      case GenerationSection.SOCIAL_AND_PLAY_DOMAIN:
        if (!localData) return null;
        return (
          <div className="section-content domain-analysis">
            <h2>Domain: Social Interaction and Play</h2>
            {renderNarrative(localData)}
          </div>
        );

      case GenerationSection.ATTENDING_SKILLS_DOMAIN:
        if (!localData) return null;
        return (
          <div className="section-content domain-analysis">
            <h2>Domain: Attending Skills</h2>
            {renderNarrative(localData)}
          </div>
        );

      case GenerationSection.DAILY_LIVING_DOMAIN:
        if (!localData) return null;
        return (
          <div className="section-content domain-analysis">
            <h2>Domain: Daily Living Skills</h2>
            {renderNarrative(localData)}
          </div>
        );

      case GenerationSection.SAFETY_SKILLS_DOMAIN:
        if (!localData) return null;
        return (
          <div className="section-content domain-analysis">
            <h2>Domain: Safety Skills</h2>
            {renderNarrative(localData)}
          </div>
        );

      case GenerationSection.FUNCTIONAL_BEHAVIOR_ASSESSMENT:
        if (!localData) return null;
        return (
          <div className="section-content behavior-assessment">
            <h2>Functional Behavior Assessment</h2>
            {localData.data_sources && (
              <div className="behavior-section">
                <h3>Data Sources</h3>
                {editingField === "data_sources" ? (
                  <span className="inline-editor">
                    <input
                      ref={inputRef}
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onBlur={handleBlur}
                    />
                    <button className="save-btn" onClick={saveInlineEdit} title="Save (Enter)">✓</button>
                    <button className="cancel-btn" onClick={cancelInlineEdit} title="Cancel (Esc)">✕</button>
                  </span>
                ) : (
                  <p
                    className={editable ? "editable-field editable-field--active" : ""}
                    onClick={() => handleFieldClick("data_sources", localData.data_sources, "Data Sources")}
                    title={editable ? "Click to edit" : undefined}
                  >
                    {localData.data_sources}
                  </p>
                )}
              </div>
            )}
            {localData.behaviors?.map((behavior: any, index: number) => (
              <div key={index} className="behavior-section">
                <h3>
                  {renderEditableNestedField(behavior.name, "behaviors", index, "name", "Behavior Name")}
                </h3>
                <p>
                  <strong>Definition:</strong>{" "}
                  {renderEditableNestedField(behavior.definition, "behaviors", index, "definition", "Definition")}
                </p>
                <p>
                  <strong>Current:</strong>{" "}
                  {renderEditableNestedField(behavior.current, "behaviors", index, "current", "Current")}
                </p>
                <p>
                  <strong>Function:</strong>{" "}
                  {renderEditableNestedField(behavior.function, "behaviors", index, "function", "Function")}
                </p>
              </div>
            ))}
          </div>
        );

      case GenerationSection.LANGUAGE_GOALS:
      case GenerationSection.SOCIAL_AND_PLAY_GOALS:
      case GenerationSection.ATTENDING_GOALS:
      case GenerationSection.DAILY_LIVING_GOALS:
      case GenerationSection.SAFETY_GOALS:
        if (!localData || !localData.goals) return null;
        return (
          <div className="section-content goals">
            <h2>
              {renderEditableField(localData.domain, "domain", "Domain Name")}
            </h2>
            {renderGoalsTable(localData.goals)}
          </div>
        );

      case GenerationSection.CAREGIVER_GOALS:
        if (!localData || !localData.goals) return null;
        return (
          <div className="section-content goals caregiver-goals">
            <h2>
              {renderEditableField(localData.domain, "domain", "Domain Name")}
            </h2>
            {localData.narrative && (
              <div
                className={`narrative-content ${editable ? "editable-narrative" : ""}`}
                onClick={() => editable && handleFieldClick("narrative", localData.narrative, "Narrative")}
                title={editable ? "Click to edit" : undefined}
              >
                <p>{localData.narrative}</p>
              </div>
            )}
            <h3>Caregiver Goals</h3>
            {renderGoalsTable(localData.goals)}
          </div>
        );

      case GenerationSection.CLINICAL_RECOMMENDATIONS:
        if (!localData) return null;
        return (
          <div className="section-content clinical-recommendations">
            <h2>Clinical Recommendations</h2>
            {localData.narrative && (
              <div
                className={`narrative-content ${editable ? "editable-narrative" : ""}`}
                onClick={() => editable && handleFieldClick("narrative", localData.narrative, "Narrative")}
                title={editable ? "Click to edit" : undefined}
              >
                <p>{localData.narrative}</p>
              </div>
            )}
            {localData.intervention_components?.length > 0 && (
              <>
                <h3>Intervention Components</h3>
                <ul>
                  {localData.intervention_components.map((component: string, index: number) => (
                    <li key={index}>
                      {editingField === `intervention_components[${index}].value` ? (
                        <span className="inline-editor">
                          <input
                            ref={inputRef}
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            onBlur={handleBlur}
                          />
                          <button className="save-btn" onClick={() => {
                            const newComponents = [...localData.intervention_components];
                            newComponents[index] = editValue;
                            updateField("intervention_components", newComponents);
                            setEditingField(null);
                            setEditValue("");
                          }} title="Save (Enter)">✓</button>
                          <button className="cancel-btn" onClick={cancelInlineEdit} title="Cancel (Esc)">✕</button>
                        </span>
                      ) : (
                        <span
                          className={editable ? "editable-field editable-field--active" : ""}
                          onClick={() => handleNestedFieldClick("intervention_components", index, "value", component, "Intervention Component")}
                          title={editable ? "Click to edit" : undefined}
                        >
                          {component}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        );

      case GenerationSection.PLAN_FOR_GENERALIZATION:
        if (!localData) return null;
        return (
          <div className="section-content generalization-plan">
            <h2>Plan for Generalization</h2>
            {renderNarrative(localData)}
          </div>
        );

      case GenerationSection.TRANSITION_PLANNING:
        if (!localData) return null;
        return (
          <div className="section-content transition-planning">
            <h2>Transition Planning</h2>
            {localData.narrative_part1 && (
              <div
                className={`narrative-content ${editable ? "editable-narrative" : ""}`}
                onClick={() => editable && handleFieldClick("narrative_part1", localData.narrative_part1, "Narrative Part 1")}
                title={editable ? "Click to edit" : undefined}
              >
                <p>{localData.narrative_part1}</p>
              </div>
            )}
            {localData.narrative_part2 && (
              <div
                className={`narrative-content ${editable ? "editable-narrative" : ""}`}
                style={{ marginTop: "16px" }}
                onClick={() => editable && handleFieldClick("narrative_part2", localData.narrative_part2, "Narrative Part 2")}
                title={editable ? "Click to edit" : undefined}
              >
                <p>{localData.narrative_part2}</p>
              </div>
            )}
            {localData.transition_skills?.length > 0 && (
              <>
                <h3>Skill Development to Support Transition Skills</h3>
                <ul>
                  {localData.transition_skills.map((skill: string, index: number) => (
                    <li key={index}>
                      {editingField === `transition_skills[${index}]` ? (
                        <span className="inline-editor">
                          <input
                            ref={inputRef}
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            onBlur={handleBlur}
                          />
                          <button className="save-btn" onClick={() => {
                            const newSkills = [...localData.transition_skills];
                            newSkills[index] = editValue;
                            updateField("transition_skills", newSkills);
                            setEditingField(null);
                            setEditValue("");
                          }} title="Save (Enter)">✓</button>
                          <button className="cancel-btn" onClick={cancelInlineEdit} title="Cancel (Esc)">✕</button>
                        </span>
                      ) : (
                        <span
                          className={editable ? "editable-field editable-field--active" : ""}
                          onClick={() => {
                            if (!editable) return;
                            setEditingField(`transition_skills[${index}]`);
                            setEditValue(skill);
                          }}
                          title={editable ? "Click to edit" : undefined}
                        >
                          {skill}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        );

      case GenerationSection.CRITERIA_FOR_DISCHARGE:
        if (!localData) return null;
        return (
          <div className="section-content discharge-criteria">
            <h2>Criteria for Discharge</h2>
            {localData.narrative_intro && (
              <div
                className={`narrative-content ${editable ? "editable-narrative" : ""}`}
                onClick={() => editable && handleFieldClick("narrative_intro", localData.narrative_intro, "Introduction")}
                title={editable ? "Click to edit" : undefined}
              >
                <p>{localData.narrative_intro}</p>
              </div>
            )}
            {localData.skills_for_discharge?.length > 0 && (
              <>
                <h3>Skills Development to Support Discharge</h3>
                <ul>
                  {localData.skills_for_discharge.map((skill: string, index: number) => (
                    <li key={index}>
                      {editingField === `skills_for_discharge[${index}]` ? (
                        <span className="inline-editor">
                          <input
                            ref={inputRef}
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            onBlur={handleBlur}
                          />
                          <button className="save-btn" onClick={() => {
                            const newSkills = [...localData.skills_for_discharge];
                            newSkills[index] = editValue;
                            updateField("skills_for_discharge", newSkills);
                            setEditingField(null);
                            setEditValue("");
                          }} title="Save (Enter)">✓</button>
                          <button className="cancel-btn" onClick={cancelInlineEdit} title="Cancel (Esc)">✕</button>
                        </span>
                      ) : (
                        <span
                          className={editable ? "editable-field editable-field--active" : ""}
                          onClick={() => {
                            if (!editable) return;
                            setEditingField(`skills_for_discharge[${index}]`);
                            setEditValue(skill);
                          }}
                          title={editable ? "Click to edit" : undefined}
                        >
                          {skill}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {localData.additional_conditions_intro && (
              <div
                className={`narrative-content ${editable ? "editable-narrative" : ""}`}
                onClick={() => editable && handleFieldClick("additional_conditions_intro", localData.additional_conditions_intro, "Additional Conditions Intro")}
                title={editable ? "Click to edit" : undefined}
              >
                <p>{localData.additional_conditions_intro}</p>
              </div>
            )}
            {localData.additional_conditions?.length > 0 && (
              <ul>
                {localData.additional_conditions.map((condition: string, index: number) => (
                  <li key={index}>
                    {editingField === `additional_conditions[${index}]` ? (
                      <span className="inline-editor">
                        <input
                          ref={inputRef}
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={handleKeyDown}
                          onBlur={handleBlur}
                        />
                        <button className="save-btn" onClick={() => {
                          const newConditions = [...localData.additional_conditions];
                          newConditions[index] = editValue;
                          updateField("additional_conditions", newConditions);
                          setEditingField(null);
                          setEditValue("");
                        }} title="Save (Enter)">✓</button>
                        <button className="cancel-btn" onClick={cancelInlineEdit} title="Cancel (Esc)">✕</button>
                      </span>
                    ) : (
                      <span
                        className={editable ? "editable-field editable-field--active" : ""}
                        onClick={() => {
                          if (!editable) return;
                          setEditingField(`additional_conditions[${index}]`);
                          setEditValue(condition);
                        }}
                        title={editable ? "Click to edit" : undefined}
                      >
                        {condition}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {localData.alternative_services && (
              <div
                className={`narrative-content ${editable ? "editable-narrative" : ""}`}
                onClick={() => editable && handleFieldClick("alternative_services", localData.alternative_services, "Alternative Services")}
                title={editable ? "Click to edit" : undefined}
              >
                <p>{localData.alternative_services}</p>
              </div>
            )}
          </div>
        );

      case GenerationSection.REQUESTED_HOURS_OF_SERVICE:
        if (!localData) return null;
        return (
          <div className="section-content service-authorization">
            <h2>
              Requested Hours of Service:{" "}
              {renderEditableField(localData.program_type, "program_type", "Program Type")}
            </h2>
            {localData.domains && (
              <div className="table-wrapper">
                <table className="treatment-plan-table">
                  <thead>
                    <tr>
                      <th style={{ width: "200px" }}>Domain</th>
                      <th>General Degree of Support Needed to Meet Goals</th>
                      <th># of Recommended Goals</th>
                    </tr>
                  </thead>
                  <tbody>
                    {localData.domains.map((domain: any, index: number) => (
                      <tr key={index} className="editable-row-container">
                        <td>
                          {renderRowControls("domains", index)}
                          <strong>
                            {renderEditableNestedField(domain.domain_name, "domains", index, "domain_name", "Domain Name")}
                          </strong>
                        </td>
                        <td>
                          {renderEditableNestedField(domain.support_level, "domains", index, "support_level", "Support Level")}
                        </td>
                        <td>
                          {renderEditableNestedField(domain.goal_count, "domains", index, "goal_count", "Goal Count")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {localData.service_codes && (
              <div className="table-wrapper">
                <table className="treatment-plan-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Description</th>
                      <th>Units Requested</th>
                    </tr>
                  </thead>
                  <tbody>
                    {localData.service_codes.map((code: any, index: number) => (
                      <tr key={index} className="editable-row-container">
                        <td>
                          {renderRowControls("service_codes", index)}
                          <strong>
                            {renderEditableNestedField(code.code, "service_codes", index, "code", "Code")}
                          </strong>
                        </td>
                        <td>
                          {renderEditableNestedField(code.description, "service_codes", index, "description", "Description")}
                        </td>
                        <td>
                          {renderEditableNestedField(code.units, "service_codes", index, "units", "Units")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );

      case GenerationSection.COORDINATION_OF_CARE:
        if (!localData) return null;
        return (
          <div className="section-content coordination-care">
            <h2>Coordination of Care</h2>
            {renderNarrative(localData)}
          </div>
        );

      case GenerationSection.CRISIS_MANAGEMENT_PLANNING:
        if (!localData) return null;
        return (
          <div className="section-content crisis-management">
            <h2>Crisis Management Planning</h2>
            {renderNarrative(localData)}
          </div>
        );

      default:
        return (
          <div className="section-content">
            <pre>{JSON.stringify(localData, null, 2)}</pre>
          </div>
        );
    }
  };

  return (
    <>
      <div className="treatment-plan-section">{renderSection()}</div>
      
      {/* Edit Modal */}
      {modalState?.isOpen && (
        <div className="edit-modal-overlay" onClick={cancelModalEdit}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-modal-header">
              <h3>{modalState.title}</h3>
              <button className="edit-modal-close" onClick={cancelModalEdit} title="Close">
                ✕
              </button>
            </div>
            <div className="edit-modal-body">
              <textarea
                className="edit-modal-textarea"
                value={modalState.value}
                onChange={(e) => setModalState({ ...modalState, value: e.target.value })}
                rows={10}
                autoFocus
              />
            </div>
            <div className="edit-modal-footer">
              <button className="edit-modal-btn edit-modal-btn-cancel" onClick={cancelModalEdit}>
                Cancel
              </button>
              <button className="edit-modal-btn edit-modal-btn-save" onClick={saveModalEdit}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TreatmentPlanSection;
